import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Target, Edit3, Trophy } from "lucide-react";
import { toast } from "sonner";

/**
 * Card fixo no topo do Funil com meta diária de prospecção de MUNICÍPIOS.
 *
 * Iter 63 · Regra: um município é contabilizado quando a vendedora registrar
 * prospecção EFETIVA (atendeu / respondeu / agendou / recusou) com ao menos
 * 4 pessoas DIFERENTES desse município no dia. Chamada não atendida, "visualizou
 * sem responder" etc. não contam.
 */
export default function MetaDiariaCard() {
  const { user, updateMe } = useAuth();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [novaMeta, setNovaMeta] = useState(user?.meta_diaria_prospeccao || 0);

  const load = () => api.get("/dashboards/meta-diaria").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  useEffect(() => {
    setNovaMeta(user?.meta_diaria_prospeccao || 0);
  }, [user?.meta_diaria_prospeccao]);

  if (!data || !data.minha) return null;
  const m = data.minha;
  const meta = m.meta;
  const realizado = m.realizado;
  const pct = m.percentual;
  const bateu = meta > 0 && realizado >= meta;
  const minPessoas = m.min_pessoas_por_municipio || 4;
  const emAndamento = m.municipios_em_andamento || [];

  // Vendedora sem meta cadastrada: mostra CTA para definir
  const isVend = user?.role === "vendedora";

  const salvarMeta = async () => {
    const n = Math.max(0, Math.min(200, Number(novaMeta) || 0));
    try {
      await updateMe({ meta_diaria_prospeccao: n });
      toast.success(n > 0 ? `Meta diária definida: ${n} município(s) 🎯` : "Meta diária removida");
      setEditOpen(false);
      load();
    } catch { toast.error("Erro ao salvar meta"); }
  };

  if (!isVend && meta === 0) return null; // não polui a tela pra outros perfis sem meta

  return (
    <>
      <Card
        className={`p-4 rounded-2xl border-2 ${bateu ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-white" : meta > 0 ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white" : "border-slate-200 bg-white"}`}
        data-testid="meta-diaria-card"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bateu ? "bg-emerald-500 text-white" : meta > 0 ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-500"}`}>
            {bateu ? <Trophy className="w-5 h-5" /> : <Target className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase font-bold tracking-widest text-slate-500">
              Meta diária de prospecção de municípios
            </div>
            {meta > 0 ? (
              <div className="font-display font-bold text-slate-900 leading-tight">
                <span className={`tabular-nums text-2xl ${bateu ? "text-emerald-600" : "text-slate-900"}`} data-testid="meta-diaria-realizado">{realizado}</span>
                <span className="text-slate-400 mx-1">/</span>
                <span className="tabular-nums text-lg text-slate-700" data-testid="meta-diaria-meta">{meta}</span>
                <span className="text-slate-500 text-sm ml-2">
                  municípios{" "}
                  {bateu ? "· META BATIDA! 🎯" : `· faltam ${Math.max(0, meta - realizado)}`}
                </span>
              </div>
            ) : (
              <div className="text-sm text-slate-600 leading-tight" data-testid="meta-diaria-sem">
                {isVend ? (
                  <>Defina sua meta diária de municípios para prospectar hoje 🎯</>
                ) : (
                  <>Meta diária não definida</>
                )}
              </div>
            )}
            {meta > 0 && (
              <div className="progress-track h-2 mt-2">
                <div
                  className={`progress-fill ${bateu ? "win" : pct < 40 ? "warn" : ""}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
            )}
            {meta > 0 && !bateu && emAndamento.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-2" data-testid="meta-diaria-andamento">
                Em andamento: {emAndamento.slice(0, 3).map(e => `${e.pessoas}/${minPessoas}`).join(" · ")}
                {emAndamento.length > 3 && ` + ${emAndamento.length - 3}`}
              </div>
            )}
          </div>
          {isVend && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              data-testid="btn-editar-meta-diaria"
              className="text-xs"
            >
              <Edit3 className="w-3 h-3 mr-1" /> {meta > 0 ? "Editar" : "Definir meta"}
            </Button>
          )}
        </div>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="meta-diaria-modal">
          <DialogHeader>
            <DialogTitle>Sua meta diária de prospecção de municípios</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              Quantos municípios diferentes você quer prospectar por dia?
            </p>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <b>📋 Como funciona:</b> um município só é contabilizado quando você registrar prospecção
              EFETIVA (ligação atendida, WhatsApp respondido, agendamento ou recusa com conversa)
              com <b>pelo menos {minPessoas} pessoas diferentes</b> daquele município no dia.
              Chamadas não atendidas e mensagens não lidas <b>não contam</b>.
            </div>
            <Input
              type="number"
              min={0}
              max={200}
              value={novaMeta}
              onChange={(e) => setNovaMeta(e.target.value)}
              data-testid="input-meta-diaria"
              placeholder="Ex.: 10"
            />
            <div className="text-xs text-slate-400">Deixe 0 para remover a meta.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={salvarMeta} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-meta-diaria">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
